import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Task, Metrics } from "@/types/dashboard";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const ERROR_STATUSES = new Set(["failed", "error"]);

/** A result/task counts towards the mean score only when it has a real judge score (1-5) and did not error. */
export function isScoredResult(r: { status?: string; accuracy_score?: number | null }): boolean {
  if (r.status && ERROR_STATUSES.has(r.status)) return false;
  return typeof r.accuracy_score === "number" && Number.isFinite(r.accuracy_score) && r.accuracy_score > 0;
}

export function isErrorResult(r: { status?: string }): boolean {
  return !!r.status && ERROR_STATUSES.has(r.status);
}

function scoredValues(rows: { status?: string; accuracy_score?: number | null }[]): number[] {
  return rows.filter(isScoredResult).map(r => r.accuracy_score as number);
}

function meanOf(scores: number[]): number {
  return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
}

/**
 * Metrics for a finished (historical) run from its results array. Unscored and failed rows are left out of
 * the mean and the scores list (matching backend metrics.mean_accuracy); failed rows are counted in `failed`.
 */
export function calculateMetricsFromResults(results: any[]): Metrics {
  const completed = results.filter(r => r.status === "completed" || r.status === "success").length;
  const failed = results.filter(isErrorResult).length;
  const scores = scoredValues(results);
  return {
    scores,
    total: results.length,
    completed,
    success: completed,
    failed,
    queued: 0,
    running: 0,
    sql_success_rate: results.length > 0
      ? (results.filter(r => r.sql_valid).length / results.length) * 100
      : 0,
    mean_accuracy: Math.round(meanOf(scores) * 100) / 100,
  };
}

/**
 * Calculate metrics from tasks object - the single source of truth
 * This ensures consistency between task list and summary stats
 */
export function calculateMetricsFromTasks(tasks: Record<string, Task>, totalQuestions?: number): Metrics {
  const taskArray = Object.values(tasks);

  const running = taskArray.filter(t => t.status === "running").length;
  const completed = taskArray.filter(t => t.status === "completed" || t.status === "success").length;
  const failed = taskArray.filter(t => t.status === "failed" || t.status === "error").length;
  const queued = taskArray.filter(t => t.status === "queued").length;

  // Only scored, non-error tasks count towards the mean (same rule as the backend metrics.mean_accuracy)
  const scores = scoredValues(taskArray);
  const mean_accuracy = meanOf(scores);

  // SQL success rate
  const total_evaluated = completed + failed;
  const sql_valid_count = taskArray.filter(t => t.sql_valid).length;
  const sql_success_rate = total_evaluated > 0
    ? (sql_valid_count / total_evaluated) * 100
    : 0;

  return {
    total: totalQuestions || taskArray.length,
    completed,
    running,
    queued,
    failed,
    success: completed,
    mean_accuracy: Math.round(mean_accuracy * 100) / 100,
    sql_success_rate: Math.round(sql_success_rate * 10) / 10,
    scores
  };
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function calculateETA(completed: number, total: number, elapsedMs: number): string {
  if (completed === 0) return "Calculating...";
  const avgTimePerTask = elapsedMs / completed;
  const remaining = total - completed;
  const etaMs = remaining * avgTimePerTask;
  return formatDuration(etaMs);
}
